import { FC, useCallback, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import Ionicons from '@expo/vector-icons/Ionicons';
import LoadingIndicator from '@/components/SharedComponents/LoadingIndicator/LoadingIndicator';
import { useNavigation } from '@react-navigation/native';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { StackScreenProps } from '@react-navigation/stack';
import { Text } from '@/components/Text';
import { Card } from './Card';
import { Container } from './Container';
import { Writing, WritingHandle } from './Writing';
import { settings as SETTINGS } from './constants';
import { PressableNeolexon } from '@/components/PressableNeolexon';
import encryptedStorage from '@/stores/encryptedStorage';
import { logError } from '@/utils/logHandling';

import { styles } from './styles';

const WARNING_TEXT_WORDSET =
  'Bei Ablenker "Aus Set" muss darauf geachtet werden, dass nicht mehrere Bilder aus dem Set zu einem Zielwort passen, z.B. wenn die Wörter "Wurst" und "Bratwurst" gleichzeitig im Set enthalten sind.';

const WARNING_TEXT_SENTENCESET =
  ' Bei Ablenker "Aus Set" muss darauf geachtet werden, dass nicht mehrere Sätze aus dem Set zu einem Zielsatz passen.';

export const AufgabenSettings: FC<
  StackScreenProps<TherapistStackParamList, 'AufgabenSettings'>
> = ({ route }) => {
  const { params } = route;
  const { setId, setType, exerciseSettings } = params;
  const settings = SETTINGS[setType as keyof typeof SETTINGS];
  const { goBack } = useNavigation();
  const settingsState = useMemo(() => exerciseSettings?.exerciseSettings, []);

  const changeLocalSetSettings = useCallback(
    async (id: string, newSettings: Record<string, unknown>) => {
      const localState = (await encryptedStorage().getItem(id)) as Record<
        string,
        unknown
      >;
      localState.exerciseSettings = newSettings;
      await encryptedStorage().setItem(id, { ...localState });
    },
    [],
  );

  // refs
  const schreibenRef = useRef<WritingHandle | null>(null);
  const sprechenRef = useRef<WritingHandle | null>(null);
  const verstehenRef = useRef<WritingHandle | null>(null);
  const lesenRef = useRef<WritingHandle | null>(null);

  // state
  const [warning, setWarning] = useState([0, 0]);
  const [isLoading, setIsLoading] = useState(false);

  // functions
  const changeWarning = useCallback((index: number, type: 'add' | null) => {
    if (type == 'add') {
      setWarning((prevState) => {
        const clonedWarning = [...prevState];
        clonedWarning[index] = 1;
        return clonedWarning;
      });
    } else {
      setWarning((prevState) => {
        const clonedWarning = [...prevState];
        clonedWarning[index] = 0;
        return clonedWarning;
      });
    }
  }, []);

  const getInnerStateAndChange = useCallback(async () => {
    new Promise((resolve, reject) => {
      try {
        const schreibenState = schreibenRef.current!.getState();
        const sprechenState = sprechenRef.current!.getState();
        const verstehenState = verstehenRef.current!.getState();
        const lesenState = lesenRef.current!.getState();

        const { outerValue, outerName } = verstehenState;

        let exerciseState;

        if (outerValue == undefined || outerName == undefined) {
          exerciseState = {
            [schreibenState.stateKey]: schreibenState.state,
            [sprechenState.stateKey]: sprechenState.state,
            [verstehenState.stateKey]: verstehenState.state,
            [lesenState.stateKey]: lesenState.state,
          };
        } else {
          exerciseState = {
            [schreibenState.stateKey]: schreibenState.state,
            [sprechenState.stateKey]: sprechenState.state,
            [verstehenState.stateKey]: verstehenState.state,
            [lesenState.stateKey]: lesenState.state,
            [outerName]: outerValue,
          };
        }
        changeLocalSetSettings(setId, exerciseState);
        // changeLocalSetSettings({ setId, newSettings: exerciseState }); hussein change
      } catch (error) {
        reject(error);
      }
    });
  }, []);

  const handleSave = useCallback(async () => {
    try {
      setIsLoading(true);
      await getInnerStateAndChange();
    } catch (error) {
      logError('Could not save state', error);
    } finally {
      setIsLoading(false);
      goBack();
    }
  }, []);

  return (
    <SafeAreaView style={styles.container}>
      <Container>
        <LoadingIndicator showLoading={isLoading} />
        <View style={styles.settings}>
          <Card>
            <Writing
              setType={setType}
              ref={schreibenRef}
              warningIndex={undefined}
              changeWarning={changeWarning}
              defaultValues={settingsState?.Schreiben}
              UI={settings[0]}
              stateKey="Schreiben"
            />
          </Card>
          <Card>
            <Writing
              setType={setType}
              ref={sprechenRef}
              warningIndex={undefined}
              changeWarning={changeWarning}
              defaultValues={settingsState?.Sprechen}
              UI={settings[1]}
              stateKey="Sprechen"
            />
          </Card>
          <Card>
            <Writing
              setType={setType}
              ref={verstehenRef}
              warningIndex={0}
              changeWarning={changeWarning}
              defaultValues={{
                value: 4,
                ...(settingsState?.Verstehen as Record<string, unknown>),
              }}
              outerValue={settingsState?.audioSpeedSetting}
              outerValueName="audioSpeedSetting"
              UI={settings[2]}
              stateKey="Verstehen"
            />
          </Card>
          <Card>
            <Writing
              setType={setType}
              ref={lesenRef}
              warningIndex={1}
              changeWarning={changeWarning}
              defaultValues={{
                value: 4,
                ...(settingsState?.Lesen as Record<string, unknown>),
              }}
              UI={settings[3]}
              stateKey="Lesen"
            />
          </Card>
          {warning.includes(1) ? (
            <View style={styles.warningContainer}>
              <Ionicons name="warning" size={30} color="#EFB83F" />
              <Text style={styles.warningText}>
                {setType == 'wordset'
                  ? WARNING_TEXT_WORDSET
                  : WARNING_TEXT_SENTENCESET}
              </Text>
            </View>
          ) : null}
        </View>
        <View style={styles.btnsContainer}>
          <PressableNeolexon onPress={goBack} style={styles.btn}>
            <Text style={styles.btnText}>Abbrechen</Text>
          </PressableNeolexon>
          <PressableNeolexon
            onPress={handleSave}
            style={[styles.btn, styles.bgBlue]}
          >
            <Text style={[styles.btnText, styles.blueText]}>Speichern</Text>
          </PressableNeolexon>
        </View>
      </Container>
    </SafeAreaView>
  );
};
